import { useState, type FormEvent } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../state/AuthContext";
import { LOCAL_SEED, type RecordRow } from "../data/localSeed";
import { PageHeader, RequestRoutingPanel, type RoutingStep } from "../components/RequestRouting";
import { hoursAgo, slaForQueueStep } from "../lib/helpdesk";

export function ProfilePage() {
  const { moduleIds, homePath } = useAuth();
  const profile = LOCAL_SEED.employee_profile?.[0];
  const [line1, setLine1] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zip, setZip] = useState("");
  const [requests, setRequests] = useState<RecordRow[]>(() => LOCAL_SEED.address_requests ?? []);
  const [selectedId, setSelectedId] = useState<string | null>(requests[0] ? String(requests[0].id) : null);

  if (!moduleIds.includes("profile")) return <Navigate to={homePath} replace />;

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!line1.trim() || !city.trim() || !state.trim() || !zip.trim()) return;
    const id = `addr-${Date.now()}`;
    const row: RecordRow = {
      id,
      new_address: `${line1}, ${city}, ${state} ${zip}`,
      status: "Submitted",
      submitted_at: new Date().toISOString(),
    };
    setRequests((prev) => [row, ...prev]);
    setSelectedId(id);
    setLine1("");
    setCity("");
    setState("");
    setZip("");
  }

  const selected = requests.find((r) => String(r.id) === selectedId);
  const steps: RoutingStep[] = selected
    ? [
        {
          id: "1",
          title: "Submitted",
          role: "You",
          status: "completed",
          at: String(selected.submitted_at || hoursAgo(8)),
          note: `Requested: ${selected.new_address}`,
        },
        {
          id: "2",
          title: "Supervisor review",
          role: "Supervisor",
          status: String(selected.status) === "Submitted" ? "current" : "completed",
          at: selected.supervisor_at ? String(selected.supervisor_at) : undefined,
          note: selected.supervisor_note ? String(selected.supervisor_note) : undefined,
        },
        {
          id: "3",
          title: "HR processing",
          role: "HR",
          status:
            String(selected.status) === "Completed"
              ? "completed"
              : String(selected.status) === "HR processing"
                ? "current"
                : "upcoming",
          at: selected.hr_at ? String(selected.hr_at) : undefined,
        },
        {
          id: "4",
          title: "Completed",
          role: "HR",
          status: String(selected.status) === "Completed" ? "completed" : "upcoming",
          at: selected.completed_at ? String(selected.completed_at) : undefined,
        },
      ]
    : [];

  const currentStep = steps.find((s) => s.status === "current");
  const sla =
    currentStep?.title === "Supervisor review"
      ? { stepTitle: "Supervisor · Approve address", ...slaForQueueStep("Supervisor", "Approve address") }
      : currentStep?.title === "HR processing"
        ? { stepTitle: "HR · Process address", ...slaForQueueStep("HR", "Process address") }
        : null;

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="space-y-6">
      <PageHeader title="My profile" subtitle="Personal details, household, training, and who supports you." />

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-white border border-black/5 p-4 space-y-3">
          <h2 className="font-semibold text-navy">Home address on file</h2>
          <p className="text-navy">{String(profile?.address || "—")}</p>
          <p className="text-sm text-slate">{String(profile?.name || "")}</p>
        </div>
        <div className="bg-white border border-black/5 p-4 space-y-2">
          <h2 className="font-semibold text-navy">Your supervisor & HR</h2>
          {(LOCAL_SEED.org_contacts ?? []).map((c) => (
            <div key={String(c.id)} className="text-sm">
              <div className="text-xs uppercase tracking-wider text-slate">{String(c.role)}</div>
              <div className="text-navy font-medium">{String(c.name)}</div>
              <div className="text-slate">{String(c.email)}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-white border border-black/5 overflow-hidden">
          <div className="px-4 py-3 border-b border-black/5 font-semibold text-navy">Emergency contacts</div>
          <table className="w-full text-sm">
            <thead className="bg-mist">
              <tr>
                <th className="text-left px-3 py-2 text-slate">Name</th>
                <th className="text-left px-3 py-2 text-slate">Relationship</th>
                <th className="text-left px-3 py-2 text-slate">Phone</th>
              </tr>
            </thead>
            <tbody>
              {(LOCAL_SEED.emergency_contacts ?? []).map((c) => (
                <tr key={String(c.id)} className="border-t border-black/5">
                  <td className="px-3 py-2">{String(c.name)}</td>
                  <td className="px-3 py-2">{String(c.relationship)}</td>
                  <td className="px-3 py-2">{String(c.phone)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="bg-white border border-black/5 overflow-hidden">
          <div className="px-4 py-3 border-b border-black/5 font-semibold text-navy">Dependents</div>
          <table className="w-full text-sm">
            <thead className="bg-mist">
              <tr>
                <th className="text-left px-3 py-2 text-slate">Name</th>
                <th className="text-left px-3 py-2 text-slate">Relationship</th>
                <th className="text-left px-3 py-2 text-slate">Birth year</th>
              </tr>
            </thead>
            <tbody>
              {(LOCAL_SEED.dependents ?? []).map((d) => (
                <tr key={String(d.id)} className="border-t border-black/5">
                  <td className="px-3 py-2">{String(d.name)}</td>
                  <td className="px-3 py-2">{String(d.relationship)}</td>
                  <td className="px-3 py-2">{String(d.birth_year)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white border border-black/5 overflow-hidden">
        <div className="px-4 py-3 border-b border-black/5 font-semibold text-navy">
          Training & compliance
        </div>
        <table className="w-full text-sm">
          <thead className="bg-mist">
            <tr>
              <th className="text-left px-3 py-2 text-slate">Assignment</th>
              <th className="text-left px-3 py-2 text-slate">Due</th>
              <th className="text-left px-3 py-2 text-slate">Status</th>
            </tr>
          </thead>
          <tbody>
            {(LOCAL_SEED.training ?? []).map((t) => {
              const overdue = String(t.status) === "Overdue" || String(t.due) < today;
              return (
                <tr key={String(t.id)} className="border-t border-black/5">
                  <td className="px-3 py-2">{String(t.name)}</td>
                  <td className="px-3 py-2">{String(t.due)}</td>
                  <td className={`px-3 py-2 ${overdue ? "text-red-700 font-medium" : ""}`}>
                    {String(t.status)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <form onSubmit={submit} className="bg-white border border-black/5 p-4 space-y-3">
          <h2 className="font-semibold text-navy">Request address change</h2>
          <p className="text-sm text-slate">Routes to your supervisor, then HR for processing.</p>
          <input
            className="w-full border border-black/10 px-3 py-2"
            placeholder="Street"
            value={line1}
            onChange={(e) => setLine1(e.target.value)}
            required
          />
          <div className="grid grid-cols-3 gap-2">
            <input
              className="border border-black/10 px-3 py-2"
              placeholder="City"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              required
            />
            <input
              className="border border-black/10 px-3 py-2"
              placeholder="ST"
              value={state}
              onChange={(e) => setState(e.target.value)}
              required
            />
            <input
              className="border border-black/10 px-3 py-2"
              placeholder="ZIP"
              value={zip}
              onChange={(e) => setZip(e.target.value)}
              required
            />
          </div>
          <button type="submit" className="px-4 py-2 bg-navy text-white text-sm">
            Submit request
          </button>
          <div className="pt-2 border-t border-black/5">
            {requests.map((r) => (
              <button
                key={String(r.id)}
                type="button"
                className="block w-full text-left text-sm py-1.5 hover:text-teal"
                onClick={() => setSelectedId(String(r.id))}
              >
                {String(r.new_address)} · {String(r.status)}
              </button>
            ))}
          </div>
        </form>
        <div className="bg-white border border-black/5 p-4">
          {selected ? (
            <RequestRoutingPanel title="Address change routing" steps={steps} sla={sla} />
          ) : (
            <p className="text-sm text-slate">Submit or select a request to see routing.</p>
          )}
        </div>
      </div>
    </div>
  );
}
